import React, { useEffect, useRef, useState } from 'react';
import { dayjs } from '@charitystack/shared';
import DonationFrequencies from '../Util/DonationFrequencies.jsx';
import ScheduledDonations from '../Util/ScheduledDonations.jsx';
import { AnimatedError } from '@charitystack/shared';
import {
  calculateScheduledDates,
  getScheduleConfigForFrequency,
  hasScheduledSelection,
  refitScheduledEndDate,
} from '../Util/scheduleConfigUtils.js';

const isSameScheduleDay = (a, b) => {
  if (!a || !b) return !a && !b;
  return dayjs(a).isSame(dayjs(b), 'day');
};

/** Shown, politely, when switching to Once removes a saved schedule. */
export const SCHEDULE_REMOVED_NOTE = "Schedule removed — one-time gifts can't repeat.";

function FormFrequencySection({
  elementID,
  containerId,
  containerClassName = 'w-full pb-4',
  scheduleContainerId,
  frequency,
  frequencies,
  selectedFrequency,
  donationType,
  setDonationType,
  onFrequencyChange,
  onSelectedFrequencyChange,
  clearFrequencyError,
  frequencyError = '',
  primaryColor,
  darkColor,
  brandColors,
  isDrawerOpen = false,
  formDisplayMode = 'dynamic',
  showScheduledDonations = false,
  defaultScheduledRange,
  scheduledDonationsOpen,
  setScheduledDonationsOpen,
  startDate,
  endDate,
  timeValue,
  onStartDateChange,
  onEndDateChange,
  onTimeChange,
  onClear,
  onEdit,
  startDateError = '',
  endDateError = '',
  timeError = '',
  autoApplyVersion = 0,
}) {
  const previousSelectedFrequencyRef = useRef(selectedFrequency);
  const [scheduleNote, setScheduleNote] = useState('');

  // QA round 2, F4: re-fits always start from the end date the DONOR chose, never from an
  // earlier re-fit, so Week → Month → Year moves it at most one re-fit away from their date.
  // `chosenEndRef` follows every end-date change that is not one of our own re-fits. It compares
  // by identity, not by day: a save from the sheet always hands over a fresh value, so whatever
  // the donor saves becomes their chosen date even when it equals the last re-fit (round 3).
  const chosenEndRef = useRef(endDate || null);
  const lastRefitRef = useRef(null);
  useEffect(() => {
    // A cleared end date is always the donor's (a re-fit never writes null), so it resets the
    // chosen end too; otherwise the next re-fit would write the old date back.
    if (!endDate || endDate !== lastRefitRef.current) chosenEndRef.current = endDate || null;
  }, [endDate]);
  const refitToFrequency = (frequency) => {
    const chosen = chosenEndRef.current;
    if (!chosen) return;
    const refitted = refitScheduledEndDate({ startDate, endDate: chosen, frequency });
    const target = refitted ?? chosen;
    if (isSameScheduleDay(target, endDate)) return;
    lastRefitRef.current = target;
    onEndDateChange?.(target);
  };
  const shouldRenderFrequencyControl =
    frequencies.length > 1 || (frequencies.length === 1 && frequencies[0] !== 'ONE_TIME');

  // The schedule editor unmounts for ONE_TIME, but this section remains mounted.
  // Keep transition ownership here so a configuration-driven ONE_TIME → recurring
  // change cannot remount the editor with another frequency's shared schedule.
  useEffect(() => {
    const previousFrequency = previousSelectedFrequencyRef.current;
    previousSelectedFrequencyRef.current = selectedFrequency;

    if (!selectedFrequency || selectedFrequency === 'ONE_TIME') return;
    if (!previousFrequency || previousFrequency === selectedFrequency) return;

    const nextScheduleDefaults = calculateScheduledDates(
      getScheduleConfigForFrequency(defaultScheduledRange, selectedFrequency),
      selectedFrequency,
    );
    const nextFrequencyOwnsSchedule = nextScheduleDefaults.isLockedSchedule
      || hasScheduledSelection(nextScheduleDefaults);
    const hasCurrentSchedule = hasScheduledSelection({ startDate, endDate, timeValue });

    // Owner call 2026-10-21: a schedule survives a change of repeat frequency, its end date
    // re-fitted to the new frequency. A frequency with its own (org) schedule still takes it.
    if (!nextFrequencyOwnsSchedule && hasCurrentSchedule) refitToFrequency(selectedFrequency);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refitToFrequency reads the same props listed here
  }, [
    defaultScheduledRange,
    endDate,
    onClear,
    onEndDateChange,
    onStartDateChange,
    onTimeChange,
    selectedFrequency,
    setScheduledDonationsOpen,
    startDate,
    timeValue,
  ]);

  const handleSelectedFrequencyChange = (nextFrequency) => {
    const isFrequencyTransition = nextFrequency !== selectedFrequency;
    const nextScheduleDefaults = calculateScheduledDates(
      getScheduleConfigForFrequency(defaultScheduledRange, nextFrequency),
      nextFrequency,
    );
    const nextFrequencyOwnsSchedule = nextScheduleDefaults.isLockedSchedule
      || hasScheduledSelection(nextScheduleDefaults);
    const hasCurrentSchedule = hasScheduledSelection({ startDate, endDate, timeValue });
    const clearSchedule = () => {
      if (onClear) {
        onClear();
      } else {
        onStartDateChange?.(null);
        onEndDateChange?.(null);
        onTimeChange?.(null);
        setScheduledDonationsOpen?.(false);
      }
    };

    // The note from a previous change goes away on the next one.
    if (isFrequencyTransition) setScheduleNote('');

    // Owner call 2026-10-21 (QA #9): the donor's schedule is kept when they change the repeat
    // frequency, its end date re-fitted to the new frequency with the picker's own snap rule.
    // Done here, at the still-mounted control, so it survives the ScheduledDonations remount.
    // Choosing Once removes it (a one-time gift has no schedule) and says so.
    if (isFrequencyTransition && hasCurrentSchedule) {
      if (nextFrequency === 'ONE_TIME') {
        clearSchedule();
        setScheduleNote(SCHEDULE_REMOVED_NOTE);
      } else if (!nextFrequencyOwnsSchedule) {
        refitToFrequency(nextFrequency);
      }
    }

    // The control path has already performed containment. Mark the transition
    // consumed so the prop-sync effect does not clear the same schedule twice.
    previousSelectedFrequencyRef.current = nextFrequency;
    onSelectedFrequencyChange(nextFrequency);
  };

  if (!shouldRenderFrequencyControl) {
    return null;
  }

  return (
    <div className={containerClassName} id={containerId}>
      <DonationFrequencies
        frequencies={frequencies}
        frequency={frequency}
        setFrequency={onFrequencyChange}
        primaryColor={primaryColor}
        darkColor={darkColor}
        elementID={elementID}
        brandColors={brandColors}
        selectedFrequency={selectedFrequency}
        setSelectedFrequency={handleSelectedFrequencyChange}
        hasError={frequencyError !== ''}
        onFrequencyChange={clearFrequencyError}
        donationType={donationType}
        setDonationType={setDonationType}
        isDrawerOpen={isDrawerOpen}
        formDisplayMode={formDisplayMode}
      />

      <AnimatedError error={frequencyError} />

      {/* Polite: announced without interrupting, and gone on the next frequency change. */}
      <div role="status" aria-live="polite" data-testid="schedule-note" className="empty:hidden">
        {scheduleNote && (
          <p className="m-0 mt-[var(--cs-space-2)] font-sans text-[length:var(--cs-text-xs-plus)] leading-[var(--cs-leading-18)] font-medium text-[color:var(--cs-text-secondary)]">
            {scheduleNote}
          </p>
        )}
      </div>

      {showScheduledDonations && selectedFrequency !== 'ONE_TIME' && (
        <div className="gap-0" id={scheduleContainerId}>
          <ScheduledDonations
            startDate={startDate}
            endDate={endDate}
            timeValue={timeValue}
            brandColors={brandColors}
            selectedFrequency={selectedFrequency}
            defaultScheduledRange={defaultScheduledRange}
            onEdit={onEdit}
            startDateError={startDateError}
            endDateError={endDateError}
            timeError={timeError}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onTimeChange={onTimeChange}
            onClear={onClear}
            scheduledDonationsOpen={scheduledDonationsOpen}
            setScheduledDonationsOpen={setScheduledDonationsOpen}
            autoApplyVersion={autoApplyVersion}
            formDisplayMode={formDisplayMode}
          />
        </div>
      )}
    </div>
  );
}

export default FormFrequencySection;
